import { AbstractAccountKeyring } from '../keyring/account_base';

/**
 * Setting flags to manage feature sets that are in use, will be upgraded or deprecated.
 * This class represents a quality group of cryptographic mechanisms that can be required or introspected after
 * loading a ciphertext. This makes it possible to require a similar quality mechanism for dumping a response in turn.
 */
export class CryptoUpgrade {
  public id: number;
  public name: string;
  public never_dump: boolean;
  public reject_load: boolean;
  public annulled: Array<CryptoUpgrade>;

  constructor(
    id: number,
    name: string,
    never_dump: boolean = false,
    reject_load: boolean = false,
    annulled?: Array<CryptoUpgrade>
  ) {
    if (id < 1) throw new Error('CU id invalid');
    this.id = id; // short name
    this.name = name;
    this.never_dump = never_dump;
    this.reject_load = reject_load;
    // The annulled field lists all the CUs that are incompatible with this one. Requiring the current CU from a
    // context essentially means to not accept the annulled ones.
    this.annulled = annulled || [];
  }
}

export const CU_BASE = new CryptoUpgrade(1, 'base');
export const CU_ANNULLED_BY_PQC_ML = new CryptoUpgrade(2, 'base-pqc'); // after CU_PQC_ML is primary for a long time, disable older versions
// The PQC_ML account keyring and crypto context upgrade is being released in active state. The clients can start using
// these asap without breaking anything because the functional usage is dependent on having an ML upgraded self-keyring
// and for shared-secret based contexts also having an ML upgraded peer-keyring.
// All PQC_ML contexts have appropriate requirements for use that ensure that they won't be used if they would break
// communication between old or not-yet-upgraded accounts:
//   - ACCOUNT_ROOT_KEY: The new config is only used for self data and when self keyring is ML, which will be upgraded
//     later when all clients of a user had time to update.
//   - ACCOUNT_SIGNATURE: The new config is only used when self keyring is ML, which will be upgraded later when all
//     clients of a user had time to update. Furthermore, a non-ML account keyring can verify the signature if the
//     client is updated to have the PQC_ML configs.
//   - DIALOGUE, NANO, NANO_ADHOC, ROOM_KEY: The new configs are only used when there is at least one shared secret from
//     ML-KEM between the accounts, which is only possible to make when both accounts have ML keyring.
//   - LOGIN_CHALLENGE, REGISTER_CHALLENGE: The new config is only used when the client is operating with an ML keyring.
// In summary: The release of the PQC_ML capabilities is permissible in active state as long as the start of
// account-keyring upgrade will be postponed until all clients have updated to be PQC_ML capable.
export const CU_PQC_ML = new CryptoUpgrade(3, 'pqc_ml'); // extension active, but unused

// Setup annulment by requirement.
CU_BASE.annulled = [CU_PQC_ML];
CU_ANNULLED_BY_PQC_ML.annulled = [CU_PQC_ML];
CU_PQC_ML.annulled = [CU_ANNULLED_BY_PQC_ML];

export const CRYPTO_UPGRADES = {
  [CU_BASE.name]: CU_BASE,
  [CU_ANNULLED_BY_PQC_ML.name]: CU_ANNULLED_BY_PQC_ML,
  [CU_PQC_ML.name]: CU_PQC_ML,
};

// This shall be advanced to VARIANT_ML after all clients have updated to be compatible.
// NOTE: The server can enforce register/upgrade to be unavailable until this is changed.
export const TARGET_ACCOUNT_KEYRING_VARIANT = AbstractAccountKeyring.VARIANT_BASE;
